<template>
  <div
    v-if="currentFilter"
    class="retailer-origin-widget"
    data-testid="retailerOriginWidget"
  >
    <AkParagraph class="retailer-origin-widget__label">
      {{ currentFilter.label }}
    </AkParagraph>

    <AkInput
      v-model="searchTerm"
      icon-left="search"
      class="retailer-origin-widget__search"
      :placeholder="t('network.filters.retailerOrigin.searchPlaceholder')"
      data-testid="retailerOriginSearch"
    />

    <div class="retailer-origin-widget__nodes">
      <template
        v-for="channel in visibleChannels"
        :key="channel.value"
      >
        <!-- Events: expandable group whose children are the brand's actual event names -->
        <div
          v-if="channel.value === EVENT_SOURCE"
          class="retailer-origin-widget__group"
        >
          <div class="retailer-origin-widget__group-header">
            <AkCheckbox
              class="retailer-origin-widget__item retailer-origin-widget__group-checkbox"
              background-color="success"
              :text="channel.label"
              :value="channel.value"
              :checked="allEventsSelected"
              :indeterminate="someEventsSelected"
              data-testid="retailerOriginEventsAll"
              @change="toggleAllEvents"
            />
            <button
              type="button"
              class="retailer-origin-widget__group-toggle"
              data-testid="retailerOriginEventsToggle"
              :aria-expanded="isEventsOpen"
              @click="eventsExpanded = !eventsExpanded"
            >
              <AkIcon
                :symbol="isEventsOpen ? 'chevron-up' : 'chevron-down'"
                size="md"
              />
            </button>
          </div>
          <AkExpander
            :keep-alive="true"
            :open="isEventsOpen"
          >
            <div class="retailer-origin-widget__children">
              <AkCheckbox
                v-for="(event, index) in visibleEvents"
                :key="index"
                class="retailer-origin-widget__item"
                background-color="success"
                :text="event.label"
                :value="event.value"
                :checked="isEventChecked(event.value)"
                :data-testid="`retailerOriginEvent-${index}`"
                @change="toggleEvent(event.value)"
              />
              <AkParagraph
                v-if="!visibleEvents.length"
                size="sm"
                class="retailer-origin-widget__empty"
              >
                {{ t('network.filters.retailerOrigin.noEvents') }}
              </AkParagraph>
            </div>
          </AkExpander>
        </div>

        <!-- Top-level acquisition channel -->
        <div
          v-else
          class="retailer-origin-widget__node"
        >
          <AkCheckbox
            class="retailer-origin-widget__item"
            background-color="success"
            :value="channel.value"
            :checked="isChannelChecked(channel.value)"
            :data-testid="`retailerOriginChannel-${channel.value}`"
            @change="toggleChannel(channel.value)"
          >
            <span
              v-if="channel.value === ANKORSTORE_SOURCE"
              class="retailer-origin-widget__logo"
              :aria-label="channel.label"
            >
              <img
                class="retailer-origin-widget__logo-symbol"
                :src="getStaticAssetUrl('/images/logo/ankorstore_symbol_black.svg')"
                alt=""
              >
              <img
                class="retailer-origin-widget__logo-wordmark"
                :src="getStaticAssetUrl('/images/logo/logo-black.svg')"
                alt=""
              >
            </span>
            <template v-else>
              {{ channel.label }}
            </template>
          </AkCheckbox>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@core/composables/use-store';
import { AkParagraph, AkInput, AkCheckbox, AkExpander, AkIcon } from '@ankorstore/design-system';
import { getStaticAssetUrl } from '@modules/shared/util/img-cdn/img-cdn';
import { ContactFilterType, type ValueContactFilter } from '../../types/api.types';
import type { ListWidgetComponent } from '../../types/filter.types';

defineOptions({ name: 'RetailerOriginWidget' });

// The acquisition channel is filtered through the fine-grained `contact.source` keyword, except
// for specific events which are refined through the `qrCampaignSourceId` facet (the "Events" group).
const ANKORSTORE_SOURCE = 'marketplace';
const EVENT_SOURCE = 'qr_campaign';
const SOURCE_FILTER = 'contact.source';
const EVENT_FILTER = 'qrCampaignSourceId';

type ChannelOption = { value: string; label: string };

const props = withDefaults(
  defineProps<{
    filter: ListWidgetComponent;
    modelValue?: ValueContactFilter | null;
  }>(),
  { modelValue: null }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ValueContactFilter): void;
}>();

const { t } = useI18n();
const store = useStore();

const searchTerm = ref('');
const eventsExpanded = ref(false);

const currentFilter = computed(() => props.filter);

const channels = computed<ChannelOption[]>(() =>
  (props.filter?.values ?? []).map((channel) => ({ value: String(channel.value), label: channel.label }))
);

const eventOptions = computed<ChannelOption[]>(() => {
  const components = store.getters['account/contactManagement/getFiltersComponents'] as
    | Array<{ attributeName?: string; values?: ChannelOption[] }>
    | undefined;
  const widget = components?.find((component) => component?.attributeName === EVENT_FILTER);

  return widget?.values ?? [];
});

const selectedSources = computed<string[]>(() =>
  Array.isArray(props.modelValue?.value) ? (props.modelValue?.value as string[]) : []
);

const selectedEvents = computed<string[]>(() => {
  const filtersState = store.getters['account/contactManagement/getFiltersState'] as
    | Record<string, ValueContactFilter>
    | undefined;
  const filter = filtersState?.[EVENT_FILTER];

  return Array.isArray(filter?.value) ? (filter.value as string[]) : [];
});

const matchesSearch = (label: string): boolean => {
  const term = searchTerm.value.trim().toLowerCase();

  return !term || label.toLowerCase().includes(term);
};

const visibleEvents = computed<ChannelOption[]>(() => eventOptions.value.filter((event) => matchesSearch(event.label)));

const visibleChannels = computed<ChannelOption[]>(() =>
  channels.value.filter((channel) => {
    if (channel.value === EVENT_SOURCE) {
      return matchesSearch(channel.label) || visibleEvents.value.length > 0;
    }

    return matchesSearch(channel.label);
  })
);

// Keep the events group open while searching or when events are already selected,
// so the relevant children are never hidden behind a collapsed header.
const isEventsOpen = computed(
  () => eventsExpanded.value || searchTerm.value.trim().length > 0 || selectedEvents.value.length > 0
);

const isChannelChecked = (value: string): boolean => selectedSources.value.includes(value);

const isEventChecked = (value: string): boolean => selectedEvents.value.includes(value);

// Tri-state "Events" parent: checked when every event is selected, indeterminate when only some are.
const allEventsSelected = computed<boolean>(
  () => eventOptions.value.length > 0 && eventOptions.value.every((event) => selectedEvents.value.includes(event.value))
);

const someEventsSelected = computed<boolean>(
  () => !allEventsSelected.value && eventOptions.value.some((event) => selectedEvents.value.includes(event.value))
);

const dispatchEvents = (events: string[]): void => {
  store.dispatch('account/contactManagement/setFilter', [
    EVENT_FILTER,
    events.length ? { type: ContactFilterType.Value, name: EVENT_FILTER, value: events } : null,
  ]);
};

const toggleAllEvents = (): void => {
  dispatchEvents(allEventsSelected.value ? [] : eventOptions.value.map((event) => event.value));
};

const toggleChannel = (value: string): void => {
  const current = selectedSources.value;
  const next = current.includes(value) ? current.filter((source) => source !== value) : [...current, value];

  emit('update:modelValue', {
    type: ContactFilterType.Value,
    name: SOURCE_FILTER,
    value: next,
  });
};

const toggleEvent = (value: string): void => {
  const current = selectedEvents.value;
  const next = current.includes(value) ? current.filter((event) => event !== value) : [...current, value];

  dispatchEvents(next);
};
</script>

<style lang="scss" scoped>
@import '@css/vue-import';

.retailer-origin-widget {
  @apply ds-text-base;

  // Label stays flush with the section edge so it lines up with the other filter labels
  // (e.g. "Discounts"); the controls below are indented to match those filters' options.
  &__label {
    @apply ds-text-base ds-text-neutral-900 ds-py-2;
  }

  &__search {
    @apply ds-mb-3 ds-px-4;
  }

  &__nodes {
    @apply ds-px-4;
  }

  &__node,
  &__group {
    @apply ds-mb-3;
  }

  &__item {
    @apply ds-mb-3 ds-text-base #{!important};
  }

  // Vertically center the checkbox indicator with its content (taller logo / single-line text).
  :deep(.ak-checkbox__sidelabel) {
    top: 50%;
    transform: translateY(-50%);
  }

  &__group-header {
    @apply ds-flex ds-items-center;
  }

  &__group-checkbox {
    @apply ds-flex-1;
  }

  &__group-toggle {
    @apply ds-flex-shrink-0 ds-bg-transparent ds-border-0 ds-p-0 ds-cursor-pointer ds-text-neutral-900;
  }

  &__children {
    @apply ds-ml-5 ds-mt-2;
  }

  &__logo {
    @apply ds-inline-flex ds-items-center ds-align-middle;
    gap: 6px;
  }

  &__logo-symbol {
    height: 24px;
  }

  &__logo-wordmark {
    height: 17px;
  }

  &__empty {
    @apply ds-text-neutral-600;
  }
}
</style>
